import React, { useCallback, useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import SearchField from "./SearchField";
import LanguageSelector from "../../common/LanguageSelector";
import GenericSelector from "./GenericSelector";
import { companySizes, companyTypes } from "../../enum";
import { useTranslation } from "react-i18next";
import StrapiService from "../../strapi/StrapiService";

export default function SideMenu({ industrieObjects, filters, setFilters, handleFlyToIndustrieObjects, onCompanySelect }) {
  const { t, i18n } = useTranslation();
  const bmweLogo = i18n.language === 'en' ? '/bmwe-logo-en.svg' : '/bmwe-logo.svg';
  const [typeOfIndustries, setTypeOfIndustries] = useState([])
  const [technologies, setTechnologies] = useState([])
  const [fieldsAndProfiles, setFieldsAndProfiles] = useState([])
  const [productsAndServices, setProductsAndServices] = useState([])
  const isMobile = useMediaQuery('(max-width:768px)');

  useEffect(() => {
    StrapiService.getProductsAndServices("", i18n.language).then(data => {
      setProductsAndServices(data.map(item => item.Name));
    });
    StrapiService.getFieldsAndProfiles("", i18n.language).then(data => {
      setFieldsAndProfiles(data.map(item => item.Name));
    });
    StrapiService.getTypeOfIndustries("", i18n.language).then(data => {
      setTypeOfIndustries(data.map(item => item.Name));
    });
    StrapiService.getTechnologies("", i18n.language).then(data => {
      setTechnologies(data.map(item => item.Name));
    });
  }, [i18n.language]);

  const onSearchChange = useCallback((val) => setFilters(f => ({ ...f, search: val })), [setFilters]);
  const onProductsChange = useCallback((val) => setFilters(f => ({ ...f, productsAndServices: val })), [setFilters]);
  const onFieldsChange = useCallback((val) => setFilters(f => ({ ...f, fieldsAndProfiles: val })), [setFilters]);
  const onTechChange = useCallback((val) => setFilters(f => ({ ...f, technologies: val })), [setFilters]);
  const onIndustriesChange = useCallback((val) => setFilters(f => ({ ...f, typeOfIndustries: val })), [setFilters]);
  const onSizesChange = useCallback((val) => setFilters(f => ({ ...f, companySizes: val })), [setFilters]);
  const onTypesChange = useCallback((val) => setFilters(f => ({ ...f, companyTypes: val })), [setFilters]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflowY: "auto",
        width: '100%',
        bgcolor: '#fff',
      }}
    >
      <Box sx={{ px: isMobile ? 2 : 3, pt: 2, pb: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <img src="/trasaar-logo.svg" alt="TraSaar" style={{ height: 88, width: 'auto' }} />
          <img src={bmweLogo} alt={i18n.language === 'en' ? 'Funded by BMWE' : 'Gefördert durch BMWE'} style={{ height: 135, width: 'auto' }} />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
          <Typography
            sx={{
              fontWeight: 700,
              color: '#0063a6',
              fontFamily: '"nowaybold", "nowayregular", sans-serif',
              fontSize: '1.1rem',
            }}
          >
            {t('site_title')}
          </Typography>
          <LanguageSelector />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <SearchField
            industrieObjects={industrieObjects}
            handleFlyToIndustrieObjects={handleFlyToIndustrieObjects}
            onChange={onSearchChange}
            onCompanySelect={onCompanySelect}
          />

          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              mb: -0.5,
              px: 0.5,
            }}
          >
            Filter
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <GenericSelector
              label={t("filters.field_name_products_and_services")}
              rawOptions={productsAndServices}
              getOptionLabel={(option) => option.title}
              industrieObjects={industrieObjects}
              matchField="product_services"
              onChange={onProductsChange}
              searchPlaceholder={t("filters.search_placeholder")}
            />
            <GenericSelector
              label={t("filters.field_name_fields_and_profiles")}
              rawOptions={fieldsAndProfiles}
              getOptionLabel={(option) => option.title}
              industrieObjects={industrieObjects}
              matchField="field_profiles"
              onChange={onFieldsChange}
              searchPlaceholder={t("filters.search_placeholder")}
            />
            <GenericSelector
              label={t("filters.field_name_technologies")}
              rawOptions={technologies}
              getOptionLabel={(option) => option.title}
              industrieObjects={industrieObjects}
              matchField="technologies"
              onChange={onTechChange}
              searchPlaceholder={t("filters.search_placeholder")}
            />
            <GenericSelector
              label={t("filters.field_name_industrie_type")}
              rawOptions={typeOfIndustries}
              getOptionLabel={(option) => option.title}
              industrieObjects={industrieObjects}
              matchField="type_of_industries"
              onChange={onIndustriesChange}
              searchPlaceholder={t("filters.search_placeholder")}
            />
            <GenericSelector
              label={t("filters.field_name_company_size")}
              rawOptions={companySizes}
              getOptionLabel={(option) => t("company_size." + option.title)}
              industrieObjects={industrieObjects}
              matchField="company_size"
              onChange={onSizesChange}
              searchPlaceholder={t("filters.search_placeholder")}
            />
            <GenericSelector
              label={t("filters.field_name_company_type")}
              rawOptions={companyTypes}
              getOptionLabel={(option) => option.title}
              industrieObjects={industrieObjects}
              matchField="company_type"
              onChange={onTypesChange}
              searchPlaceholder={t("filters.search_placeholder")}
            />
          </Box>
        </Box>
      </Box>

    </Box>
  );
}
